import { Add, DeleteOutline, Edit, Link as LinkIcon, Refresh } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React from 'react';
import { formatarDataHora } from 'utils/formatters';

import { useAdminProdutosAfiliadosRules } from './AdminProdutosAfiliadosPage.rules';
import { BooleanFilter } from './AdminProdutosAfiliadosPage.types';

const AdminProdutosAfiliadosPage: React.FC = () => {
  const {
    loadingTemplates,
    filters,
    produtos,
    loadingProdutos,
    erro,
    feedback,
    pageSize,
    totalProdutos,
    categoriasRapidas,
    categoriaAtiva,
    parceiroAtivo,
    faixaPrecoAtivaChave,
    paginaNormalizada,
    dialogOpen,
    salvando,
    produtoEditando,
    formState,
    filtrosAplicadosResumo,
    templatesDisponiveis,
    precoChips,
    setPage,
    setPageSize,
    atualizarFiltro,
    handlePrecoFiltroChange,
    aplicarFiltros,
    limparFiltros,
    aplicarCategoriaRapida,
    aplicarParceiroRapido,
    aplicarFaixaPrecoRapida,
    abrirDialogCriar,
    abrirDialogEditar,
    fecharDialog,
    atualizarForm,
    salvarProduto,
    excluirProduto,
  } = useAdminProdutosAfiliadosRules();

  const lineClamp = (lines: number) => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
  });

  const formatarMoeda = (valor: number) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(valor);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Catálogo de Produtos Afiliados
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Centralize aqui os produtos recomendados, defina destaques e vincule-os aos templates de
            listas.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button startIcon={<Add />} variant="contained" onClick={abrirDialogCriar}>
            Novo produto
          </Button>
        </Stack>
      </Stack>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {feedback && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {feedback}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
            <TextField
              label="Buscar"
              value={filters.search}
              onChange={(e) => atualizarFiltro('search', e.target.value)}
              fullWidth
            />
            <TextField
              label="Categoria"
              value={filters.categoria}
              onChange={(e) => atualizarFiltro('categoria', e.target.value)}
              fullWidth
            />
            <TextField
              label="Parceiro"
              value={filters.parceiro}
              onChange={(e) => atualizarFiltro('parceiro', e.target.value)}
              fullWidth
            />
            <TextField
              label="Preço mínimo"
              value={filters.minPreco}
              onChange={(e) => handlePrecoFiltroChange('minPreco', e.target.value)}
              fullWidth
              inputMode="decimal"
              placeholder="Ex: 100"
            />
            <TextField
              label="Preço máximo"
              value={filters.maxPreco}
              onChange={(e) => handlePrecoFiltroChange('maxPreco', e.target.value)}
              fullWidth
              inputMode="decimal"
              placeholder="Ex: 500"
            />
            <FormControl fullWidth>
              <InputLabel id="filtro-template-label">Template</InputLabel>
              <Select
                labelId="filtro-template-label"
                label="Template"
                value={filters.templateSlug}
                onChange={(e) => atualizarFiltro('templateSlug', e.target.value)}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {templatesDisponiveis.map((template) => (
                  <MenuItem key={template.slug} value={template.slug}>
                    {template.titulo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="filtro-ativo-label">Ativo</InputLabel>
              <Select
                labelId="filtro-ativo-label"
                label="Ativo"
                value={filters.ativo}
                onChange={(e) => atualizarFiltro('ativo', e.target.value as BooleanFilter)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="true">Sim</MenuItem>
                <MenuItem value="false">Não</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="filtro-destaque-label">Destaque</InputLabel>
              <Select
                labelId="filtro-destaque-label"
                label="Destaque"
                value={filters.destaque}
                onChange={(e) => atualizarFiltro('destaque', e.target.value as BooleanFilter)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="true">Sim</MenuItem>
                <MenuItem value="false">Não</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {filtrosAplicadosResumo}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={limparFiltros}>
              Limpar
            </Button>
            <Button variant="contained" onClick={aplicarFiltros} startIcon={<Refresh />}>
              Aplicar filtros
            </Button>
          </Stack>
        </CardActions>
      </Card>

      {(categoriasRapidas.length > 0 || precoChips.length > 0) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              {categoriasRapidas.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Categorias:
                  </Typography>
                  <Chip
                    label="Todas"
                    size="small"
                    color={categoriaAtiva ? 'default' : 'primary'}
                    variant={categoriaAtiva ? 'outlined' : 'filled'}
                    onClick={() => aplicarCategoriaRapida('')}
                  />
                  {categoriasRapidas.map((categoria) => (
                    <Chip
                      key={categoria}
                      label={categoria}
                      size="small"
                      color={categoriaAtiva === categoria ? 'primary' : 'default'}
                      variant={categoriaAtiva === categoria ? 'filled' : 'outlined'}
                      onClick={() => aplicarCategoriaRapida(categoria)}
                    />
                  ))}
                </Stack>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Afiliado:
                </Typography>
                <Chip
                  label="Todos"
                  size="small"
                  color={parceiroAtivo ? 'default' : 'primary'}
                  variant={parceiroAtivo ? 'outlined' : 'filled'}
                  onClick={() => aplicarParceiroRapido('')}
                />
                {['Mercado Livre', 'Amazon', 'Magazine Luiza'].map((parceiro) => (
                  <Chip
                    key={parceiro}
                    label={parceiro}
                    size="small"
                    color={parceiroAtivo === parceiro ? 'primary' : 'default'}
                    variant={parceiroAtivo === parceiro ? 'filled' : 'outlined'}
                    onClick={() => aplicarParceiroRapido(parceiro)}
                  />
                ))}
              </Stack>

              {precoChips.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Faixa de preço:
                  </Typography>
                  <Chip
                    label="Todos"
                    size="small"
                    color={faixaPrecoAtivaChave === '|' ? 'primary' : 'default'}
                    variant={faixaPrecoAtivaChave === '|' ? 'filled' : 'outlined'}
                    onClick={() => aplicarFaixaPrecoRapida(undefined, undefined)}
                  />
                  {precoChips.map((chip) => {
                    const minKey = typeof chip.min === 'number' ? chip.min.toFixed(2) : '';
                    const maxKey = typeof chip.max === 'number' ? chip.max.toFixed(2) : '';
                    const chave = `${minKey}|${maxKey}`;
                    return (
                      <Chip
                        key={chave || chip.label}
                        label={chip.label}
                        size="small"
                        color={faixaPrecoAtivaChave === chave ? 'primary' : 'default'}
                        variant={faixaPrecoAtivaChave === chave ? 'filled' : 'outlined'}
                        onClick={() => aplicarFaixaPrecoRapida(chip.min, chip.max)}
                      />
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          {loadingProdutos ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : produtos.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" gutterBottom>
                Nenhum produto encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ajuste os filtros ou cadastre um novo produto afiliado.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                overflowX: 'auto',
                '& td, & th': { verticalAlign: 'top' },
              }}
            >
              <TablePagination
                component="div"
                count={totalProdutos}
                page={paginaNormalizada}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  const novoTamanho = Number.parseInt(event.target.value, 10);
                  setPageSize(Number.isNaN(novoTamanho) ? 25 : novoTamanho);
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Itens por página"
              />
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Imagem</TableCell>
                    <TableCell>Produto</TableCell>
                    <TableCell>Parceiro</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Preço</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Templates</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Atualizado</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {produtos.map((produto) => (
                    <TableRow key={produto.id} hover>
                      <TableCell sx={{ width: 96 }}>
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          {produto.imagemUrl ? (
                            <Box
                              component="img"
                              src={produto.imagemUrl}
                              alt={produto.nome}
                              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Sem foto
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2" sx={lineClamp(2)} title={produto.nome}>
                            {produto.nome}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ...lineClamp(2), wordBreak: 'break-all' }}
                            title={produto.urlAfiliado}
                          >
                            {produto.urlAfiliado}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 140 }}>
                        <Typography
                          variant="body2"
                          sx={lineClamp(2)}
                          title={produto.parceiro || '—'}
                        >
                          {produto.parceiro || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Typography
                          variant="body2"
                          sx={lineClamp(2)}
                          title={produto.categoria || '—'}
                        >
                          {produto.categoria || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatarMoeda(produto.preco)}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        {produto.tags.length ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ maxHeight: 60, overflow: 'hidden' }}
                          >
                            {produto.tags.map((tag) => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        {produto.templatesSugestoes.length ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ maxHeight: 60, overflow: 'hidden' }}
                          >
                            {produto.templatesSugestoes.map((item) => (
                              <Chip
                                key={`${produto.id}-${item.templateSlug}`}
                                label={item.templateSlug}
                                size="small"
                                color={item.destaque ? 'primary' : 'default'}
                                icon={<LinkIcon fontSize="inherit" />}
                                title={`Template ${item.templateSlug}${
                                  typeof item.ordem === 'number' ? ` • ordem ${item.ordem}` : ''
                                }${item.destaque ? ' • destaque' : ''}`}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip
                            label={produto.ativo ? 'Ativo' : 'Inativo'}
                            color={produto.ativo ? 'success' : 'default'}
                            size="small"
                          />
                          {produto.destaque && (
                            <Chip label="Destaque" color="warning" size="small" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatarDataHora(produto.updatedAt) ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Editar produto">
                            <IconButton onClick={() => abrirDialogEditar(produto)} size="small">
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remover produto">
                            <IconButton
                              onClick={() => excluirProduto(produto)}
                              size="small"
                              color="error"
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalProdutos}
                page={paginaNormalizada}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  const novoTamanho = Number.parseInt(event.target.value, 10);
                  setPageSize(Number.isNaN(novoTamanho) ? 25 : novoTamanho);
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Itens por página"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={fecharDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {produtoEditando ? 'Editar produto afiliado' : 'Novo produto afiliado'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                label="Nome do produto"
                fullWidth
                value={formState.nome}
                onChange={(e) => atualizarForm('nome', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Preço"
                type="number"
                fullWidth
                value={formState.preco}
                onChange={(e) => atualizarForm('preco', e.target.value)}
                inputProps={{ min: '0', step: '0.01' }}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="URL do afiliado"
                fullWidth
                value={formState.urlAfiliado}
                onChange={(e) => atualizarForm('urlAfiliado', e.target.value)}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Descrição"
                fullWidth
                multiline
                minRows={3}
                value={formState.descricao}
                onChange={(e) => atualizarForm('descricao', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Imagem (URL)"
                fullWidth
                value={formState.imagemUrl}
                onChange={(e) => atualizarForm('imagemUrl', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Parceiro"
                fullWidth
                value={formState.parceiro}
                onChange={(e) => atualizarForm('parceiro', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Categoria"
                fullWidth
                value={formState.categoria}
                onChange={(e) => atualizarForm('categoria', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Tags (separe por vírgula)"
                fullWidth
                value={formState.tagsRaw}
                onChange={(e) => atualizarForm('tagsRaw', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Origem dos dados"
                fullWidth
                value={formState.origemDados}
                onChange={(e) => atualizarForm('origemDados', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="ID externo"
                fullWidth
                value={formState.externalId}
                onChange={(e) => atualizarForm('externalId', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth disabled={loadingTemplates}>
                <InputLabel id="templates-select-label">Templates vinculados</InputLabel>
                <Select
                  labelId="templates-select-label"
                  multiple
                  label="Templates vinculados"
                  value={formState.templateSlugs}
                  onChange={(e) => atualizarForm('templateSlugs', e.target.value as string[])}
                  renderValue={(selected) => (selected as string[]).join(', ')}
                >
                  {templatesDisponiveis.map((template) => (
                    <MenuItem key={template.slug} value={template.slug}>
                      {template.titulo}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Vincule o produto aos templates que receberão sugestões automáticas.
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Ordem padrão nas sugestões"
                type="number"
                fullWidth
                value={formState.ordemSugestao}
                onChange={(e) => atualizarForm('ordemSugestao', e.target.value)}
                disabled={!formState.templateSlugs.length}
              />
            </Grid>
            <Grid size={12}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formState.ativo}
                      onChange={(e) => atualizarForm('ativo', e.target.checked)}
                    />
                  }
                  label="Produto ativo"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formState.destaque}
                      onChange={(e) => atualizarForm('destaque', e.target.checked)}
                    />
                  }
                  label="Destacar no catálogo"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formState.estoqueDisponivel}
                      onChange={(e) => atualizarForm('estoqueDisponivel', e.target.checked)}
                    />
                  }
                  label="Estoque disponível"
                />
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialog} disabled={salvando}>
            Cancelar
          </Button>
          <Button onClick={salvarProduto} variant="contained" disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
        {erro && (
          <Box px={3} pb={2}>
            <Alert severity="error">{erro}</Alert>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default AdminProdutosAfiliadosPage;
